import React, { useEffect, useRef, useState } from "react";

import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";

import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  UploadFile as UploadFileIcon,
  PictureAsPdf as PictureAsPdfIcon,
  InsertPhoto as InsertPhotoIcon,
  WarningAmber as WarningAmberIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
} from "@mui/icons-material";

import { FormatDateTimeAnneeMoisJour } from "../../../../../utils/formateDate";
import { DeleteComplaintImageById } from "../../../api/ComplaintApi";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

// ============================================================
// LOCAL IMAGE PREVIEW
// ============================================================

function LocalImagePreview({ file, fileName }) {
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  if (!previewUrl) {
    return (
      <Box
        sx={{
          width: 90,
          height: 90,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
          backgroundColor: "action.hover",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <InsertPhotoIcon color="action" />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={previewUrl}
      alt={fileName}
      sx={{
        width: 90,
        height: 90,
        flexShrink: 0,
        objectFit: "cover",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
      }}
    />
  );
}

// ============================================================
// MODIFY COMPLAINT
// ============================================================

function ModifyComplaint({
  open,
  onClose,
  complaintData,
  setAlertMessage,
  setAlertType,
  setAlertOpen,
}) {
  // ============================================================
  // STYLE
  // ============================================================

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 850,
    maxWidth: "95vw",
    height: 850,
    maxHeight: "95vh",
    overflow: "auto",
    bgcolor: "background.paper",
    borderRadius: 3,
    border: "1px solid",
    borderColor: "divider",
    boxShadow: 24,
    p: 4,
  };

  // ============================================================
  // REDUX
  // ============================================================
  const userState = useSelector((state) => state.user);

  // ============================================================
  // TRANSLATION
  // ============================================================
  const { t } = useTranslation(["Casserole", "Common"]);

  // ============================================================
  // REFS
  // ============================================================
  const modalContentRef = useRef(null);
  const addFilesInputRef = useRef(null);
  const replaceFileInputRef = useRef(null);

  // ============================================================
  // STATES
  // ============================================================

  const [complaint, setComplaint] = useState(null);
  const [formValues, setFormValues] = useState({
    id: "",
    status: null,
    quantity: "",
    unit: "",
    comment: "",
    credit: 0,
    typeCredit: null,
  });

  const [statusses, setStatusses] = useState([]);
  const [creditTypes, setCreditTypes] = useState([]);

  // ============================================================
  // EXISTING FILES
  // ============================================================
  const [images, setImages] = useState([]);

  // ============================================================
  // NEW FILES
  // ============================================================
  const [files, setFiles] = useState([]);

  // ============================================================
  // DELETE
  // ============================================================
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // ============================================================
  // REPLACE
  // ============================================================
  const [fileToReplace, setFileToReplace] = useState(null);

  // ============================================================
  // ALERT
  // ============================================================
  const showAlert = (message, type = "error") => {
    if (setAlertMessage) {
      setAlertMessage(message);
    }

    if (setAlertType) {
      setAlertType(type);
    }

    if (setAlertOpen) {
      setAlertOpen(true);
    }
  };

  // ============================================================
  // GET LOCAL FILE TYPE
  // ============================================================
  const getLocalFileType = (file) => {
    if (!file) {
      return "file";
    }

    if (file.type?.startsWith("image/")) {
      return "image";
    }

    if (file.type === "application/pdf") {
      return "pdf";
    }

    return "file";
  };
  // ============================================================
  // FETCH COMPLAINT
  // ============================================================

  useEffect(() => {
    if (!open || !complaintData?.id || !userState.token) {
      return;
    }

    let isMounted = true;
    const fetchComplaintById = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/Casserole/GetComplaintById/${complaintData.id}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const responseText = await response.text();
        if (!responseText.trim()) {
          throw new Error("The server returned an empty response.");
        }
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (error) {
          console.error(
            "Invalid JSON returned by GetComplaintById:",
            responseText,
          );
          throw new Error("Invalid JSON response from server.");
        }

        if (!data) {
          throw new Error("Complaint data is empty.");
        }

        if (!isMounted) {
          return;
        }

        const formattedData = {
          id: data.id,
          date: data.dateCreation
            ? FormatDateTimeAnneeMoisJour(data.dateCreation)
            : "",

          restaurant: data.nomEmplacement || "",
          phoneNumber: data.noTel || "",
          personInCharge: data.nomResponsable || "",

          product:
            userState.lang === "EN"
              ? [data.idItemAccPac, data.nomEn, data.format]
                  .filter(
                    (value) =>
                      value !== null &&
                      value !== undefined &&
                      String(value).trim() !== "",
                  )
                  .join(" | ")
              : [data.idItemAccPac, data.nomFr, data.format]
                  .filter(
                    (value) =>
                      value !== null &&
                      value !== undefined &&
                      String(value).trim() !== "",
                  )
                  .join(" | "),

          problem: data.probleme || "",
          quantity: data.quantite ?? "",
          comment: data.comment || "",
          unit: data.unit || "",
          supplier: data.fournisseur || "",
          typeCredit: data.idCrediteur ?? null,
          credit: data.credit ?? 0,
          transporteur: data.transporteur || "",

          status:
            userState.lang === "EN" ? data.statutEn || "" : data.statutFr || "",

          noLot: data.noLot || "",
          productionDate: data.dateProduction || "",
          expirationDate: data.dateExpiration || "",
        };

        setComplaint(formattedData);
        setFormValues((previous) => ({
          ...previous,

          id: formattedData.id,
          quantity: formattedData.quantity,
          unit: formattedData.unit,

          comment: `${formattedData.comment || ""}\n[${new Date().toLocaleString()}] [${
            userState.username || ""
          }] : `,

          credit: formattedData.credit,
        }));
      } catch (error) {
        console.error("Error fetching complaint:", error);

        if (!isMounted) {
          return;
        }

        setComplaint(null);
        showAlert(`Unable to fetch complaint: ${error.message}`, "error");
      }
    };

    fetchComplaintById();

    return () => {
      isMounted = false;
    };
  }, [
    open,
    complaintData?.id,
    userState.token,
    userState.lang,
    userState.username,
  ]);

  // ============================================================
  // FETCH COMPLAINT FILES
  // ============================================================

  useEffect(() => {
    if (!open || !complaintData?.id || !userState.token) {
      return;
    }

    let isMounted = true;

    const fetchComplaintImageById = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/Casserole/GetImagesComplaintById/${complaintData.id}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          },
        );

        if (response.status === 204) {
          if (isMounted) {
            setImages([]);
          }

          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const text = await response.text();

        if (!text.trim()) {
          if (isMounted) {
            setImages([]);
          }

          return;
        }

        let data;

        try {
          data = JSON.parse(text);
        } catch (error) {
          console.error("Response is not valid JSON:", text);

          throw new Error("Invalid JSON response.");
        }

        if (!Array.isArray(data)) {
          if (isMounted) {
            setImages([]);
          }

          return;
        }

        const formattedFiles = data
          .map((item, index) => {
            if (!item || !item.image) {
              return null;
            }

            const imageId = item.id;
            const byteArray = item.image;

            if (!imageId || typeof byteArray !== "string") {
              return null;
            }

            try {
              if (byteArray.startsWith("JVBER")) {
                return {
                  id: imageId,
                  itemPlainteId: item.itemPlainteId,
                  type: "pdf",
                  url: `data:application/pdf;base64,${byteArray}`,
                  name: `Complaint_${complaintData.id}_${index + 1}.pdf`,
                  isExisting: true,
                  isReplacement: false,
                };
              }

              return {
                id: imageId,
                itemPlainteId: item.itemPlainteId,
                type: "image",
                url: `data:image/png;base64,${byteArray}`,
                name: `Complaint_${complaintData.id}_${index + 1}.png`,
                isExisting: true,
                isReplacement: false,
              };
            } catch (error) {
              console.error(`Error processing file ${index}:`, error);

              return null;
            }
          })
          .filter(Boolean);
        if (isMounted) {
          setImages(formattedFiles);
        }
      } catch (error) {
        console.error("Error fetching complaint files:", error);

        if (isMounted) {
          setImages([]);

          showAlert(
            "Unable to load complaint files. Please try again.",
            "error",
          );
        }
      }
    };

    fetchComplaintImageById();

    return () => {
      isMounted = false;
    };
  }, [open, complaintData?.id, userState.token]);

  // ============================================================
  // FETCH STATUS + CREDIT TYPES
  // ============================================================

  useEffect(() => {
    if (!open || !userState.token) {
      return;
    }

    let isMounted = true;
    const fetchComplaintStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/Casserole/GetComplaintStatus`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const text = await response.text();
        if (!text.trim()) {
          throw new Error("Empty response.");
        }

        const data = JSON.parse(text);
        if (!Array.isArray(data)) {
          throw new Error("Invalid status response.");
        }

        if (isMounted) {
          setStatusses(data);
        }
      } catch (error) {
        console.error("Error fetching complaint status:", error);

        if (isMounted) {
          showAlert("Unable to fetch complaint statuses.", "error");
        }
      }
    };

    const fetchComplaintCreditTypes = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/Casserole/GetComplaintCreditTypes`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const text = await response.text();
        if (!text.trim()) {
          throw new Error("Empty response.");
        }

        const data = JSON.parse(text);
        if (!Array.isArray(data)) {
          throw new Error("Invalid credit types response.");
        }

        if (isMounted) {
          setCreditTypes(data);
        }
      } catch (error) {
        console.error("Error fetching complaint credit types:", error);

        if (isMounted) {
          showAlert("Unable to fetch complaint credit types.", "error");
        }
      }
    };

    fetchComplaintStatus();
    fetchComplaintCreditTypes();

    return () => {
      isMounted = false;
    };
  }, [open, userState.token]);

  // ============================================================
  // INITIALIZE STATUS / CREDIT TYPE
  // ============================================================

  useEffect(() => {
    if (!complaint) {
      return;
    }

    const initialStatus = statusses.find(
      (status) =>
        (userState.lang === "EN" ? status.textEn : status.textFr) ===
        complaint.status,
    );

    const initialCreditType = creditTypes.find(
      (creditType) => creditType.id === complaint.typeCredit,
    );

    setFormValues((previous) => ({
      ...previous,
      status: initialStatus || null,
      typeCredit: initialCreditType || null,
    }));
  }, [complaint, statusses, creditTypes, userState.lang]);

  // ============================================================
  // RESET
  // ============================================================

  useEffect(() => {
    if (!open) {
      setComplaint(null);
      setImages([]);
      setFiles([]);
      setDeleteDialogOpen(false);
      setFileToDelete(null);
      setFileToReplace(null);
      setFormValues({
        id: "",
        status: null,
        quantity: "",
        unit: "",
        comment: "",
        credit: 0,
        typeCredit: null,
      });
    }
  }, [open]);

  // ============================================================
  // INPUT CHANGE
  // ============================================================

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormValues((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  // ============================================================
  // ADD DOCUMENTS
  // ============================================================

  const handleOpenAddDocuments = () => {
    if (addFilesInputRef.current) {
      addFilesInputRef.current.value = "";

      addFilesInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (selectedFiles.length === 0) {
      return;
    }

    setFiles((previousFiles) => [...previousFiles, ...selectedFiles]);
    event.target.value = "";

    showAlert(
      `${selectedFiles.length} document(s) added. Save the complaint to apply the changes.`,
      "success",
    );
  };

  // ============================================================
  // REMOVE NEW FILE
  // ============================================================

  const handleRemoveFile = (indexToRemove) => {
    setFiles((previousFiles) =>
      previousFiles.filter((_, index) => index !== indexToRemove),
    );
  };

  // ============================================================
  // DELETE EXISTING FILE
  // ============================================================

  const handleRemoveExistingFile = (indexToRemove) => {
    const file = images[indexToRemove];

    if (!file) {
      return;
    }

    setFileToDelete({
      file,
      index: indexToRemove,
    });

    setDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);

    setFileToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) {
      return;
    }

    const { index } = fileToDelete;
    const deletedFile = images[index];

    if (!deletedFile) {
      return;
    }

    // =====================================================
    // VÉRIFIER L'ID DE L'IMAGE
    // =====================================================

    if (!deletedFile.id) {
      showAlert(
        userState.lang === "EN"
          ? "Unable to delete the image: image ID is missing."
          : "Impossible de supprimer l'image : l'identifiant de l'image est manquant.",
        "error",
      );

      return;
    }

    try {
      // ===================================================
      // DELETE API
      // ===================================================

      await DeleteComplaintImageById(
        userState.token,
        deletedFile.id,
        userState.lang,
      );

      // ===================================================
      // SUPPRIMER DU STATE REACT
      // ===================================================

      setImages((previousImages) =>
        previousImages.filter((_, currentIndex) => currentIndex !== index),
      );

      // ===================================================
      // FERMER LE DIALOG
      // ===================================================

      setDeleteDialogOpen(false);
      setFileToDelete(null);

      // ===================================================
      // SUCCESS
      // ===================================================

      showAlert(
        userState.lang === "EN"
          ? "The image has been deleted successfully."
          : "L'image a été supprimée avec succès.",
        "success",
      );
    } catch (error) {
      console.error("Error deleting complaint image:", error);

      // ===================================================
      // ERROR
      // ===================================================

      showAlert(error.message, "error");
    }
  };

  // ============================================================
  // REPLACE FILE
  // ============================================================

  const handleOpenReplaceFile = (index) => {
    setFileToReplace(index);

    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.value = "";
      replaceFileInputRef.current.click();
    }
  };

  const handleReplaceFile = (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (fileToReplace === null || fileToReplace === undefined) {
      return;
    }

    const oldFile = images[fileToReplace];

    const localUrl = URL.createObjectURL(selectedFile);

    const newFileType = getLocalFileType(selectedFile);

    const replacementFile = {
      type: newFileType,

      url: localUrl,

      name: selectedFile.name,

      isExisting: false,

      isReplacement: true,

      localFile: selectedFile,

      replacedFileName: oldFile?.name || "",
    };

    setImages((previousImages) =>
      previousImages.map((file, index) =>
        index === fileToReplace ? replacementFile : file,
      ),
    );

    if (oldFile?.url && oldFile.url.startsWith("blob:")) {
      URL.revokeObjectURL(oldFile.url);
    }

    setFileToReplace(null);

    event.target.value = "";

    showAlert(
      "Document updated successfully. Save the complaint to apply the changes.",
      "success",
    );
  };

  // ============================================================
  // DOWNLOAD EXISTING / REPLACED
  // ============================================================

  const handleDownloadExistingFile = (file, index) => {
    if (!file?.url) {
      console.error("Cannot download file: URL is missing.", file);

      return;
    }

    try {
      const link = document.createElement("a");

      link.href = file.url;

      link.download = file.name || `complaint-file-${index + 1}`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading existing file:", error);

      showAlert("Failed to download the file.", "error");
    }
  };

  // ============================================================
  // DOWNLOAD LOCAL
  // ============================================================

  const handleDownloadLocalFile = (file) => {
    if (!file) {
      return;
    }

    try {
      const url = URL.createObjectURL(file);

      const link = document.createElement("a");

      link.href = url;

      link.download = file.name || "complaint-file";

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error("Error downloading local file:", error);

      showAlert("Failed to download the file.", "error");
    }
  };

  // ============================================================
  // OPEN PDF
  // ============================================================

  const handleOpenPDF = (file) => {
    if (!file?.url) {
      return;
    }

    try {
      window.open(file.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Error opening PDF:", error);

      showAlert("Unable to open PDF.", "error");
    }
  };

  // ============================================================
  // SUBMIT
  // ============================================================

  const handleSubmit = async () => {
    if (!formValues.id) {
      showAlert("Complaint ID is missing.", "error");

      return;
    }

    try {
      const formData = new FormData();

      formData.append("Id", String(formValues.id));

      formData.append("IdStatut", String(formValues.status?.id || 0));

      formData.append("Unit", formValues.unit || "");

      formData.append("Comment", formValues.comment || "");

      formData.append("Credit", String(formValues.credit || 0));

      formData.append("IdCrediteur", String(formValues.typeCredit?.id || 0));

      formData.append("Quantity", String(formValues.quantity || 0));

      // ========================================================
      // NEW FILES
      // ========================================================

      files.forEach((file) => {
        if (file instanceof File) {
          formData.append("Files", file);
        }
      });

      // ========================================================
      // REPLACED FILES
      // ========================================================

      images.forEach((file) => {
        if (file?.isReplacement && file?.localFile instanceof File) {
          formData.append("Files", file.localFile);
        }
      });

      console.log("Files to upload:", files);

      console.log(
        "Replaced files:",
        images.filter((file) => file?.isReplacement),
      );

      // ========================================================
      // API
      // ========================================================

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Casserole/UpdateComplaintById`,
        {
          method: "PUT",

          headers: {
            Authorization: `Bearer ${userState.token}`,
          },

          body: formData,
        },
      );

      if (!response.ok) {
        const errorText = await response.text();

        console.error("Update complaint failed:", response.status, errorText);

        throw new Error(`HTTP ${response.status}`);
      }

      const responseText = await response.text();

      let data = null;

      if (responseText.trim()) {
        try {
          data = JSON.parse(responseText);
        } catch (error) {
          console.log("Update response is not JSON:", responseText);
        }
      }

      console.log("Complaint updated:", data);

      showAlert("Complaint updated successfully!", "success");

      onClose();
    } catch (error) {
      console.error("Error updating complaint:", error);

      showAlert("An error occurred while updating the complaint.", "error");
    }
  };

  // ============================================================
  // PRINT
  // ============================================================

  const handlePrint = () => {
    if (!modalContentRef.current || !complaint) {
      alert("Nothing to print. Please check the content.");

      return;
    }

    const safeComment = String(formValues.comment || "").replace(/\n/g, "<br>");

    const printableContent = `
      <html>
        <head>
          <title>Print Complaint</title>

          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              background-color: #f4f4f4;
            }

            .print-box {
              width: 90%;
              max-width: 800px;
              margin: auto;
              padding: 20px;
              background: white;
              border-radius: 10px;
            }

            h2 {
              text-align: center;
              color: #333;
            }

            .section {
              border: 1px solid #ccc;
              border-radius: 8px;
              padding: 15px;
              margin-bottom: 20px;
            }

            .field {
              margin-bottom: 10px;
            }

            .field-label {
              font-weight: bold;
              color: #555;
            }

            .field-value {
              margin: 0;
              color: #333;
            }

            @media print {
              body {
                background-color: white;
              }
            }
          </style>
        </head>

        <body>

          <div class="print-box">

            <h2>
              Complaint no : ${complaint.id}
            </h2>

            <div class="section">

              <div class="field">
                <span class="field-label">ID:</span>
                <span class="field-value">
                  ${complaint.id || ""}
                </span>
              </div>

              <div class="field">
                <span class="field-label">Date:</span>
                <span class="field-value">
                  ${complaint.date || ""}
                </span>
              </div>

              <div class="field">
                <span class="field-label">Restaurant:</span>
                <span class="field-value">
                  ${complaint.restaurant || ""}
                </span>
              </div>

              <div class="field">
                <span class="field-label">Item:</span>
                <span class="field-value">
                  ${complaint.product || ""}
                </span>
              </div>

              <div class="field">
                <span class="field-label">Supplier:</span>
                <span class="field-value">
                  ${complaint.supplier || ""}
                </span>
              </div>

              <div class="field">
                <span class="field-label">Transporteur:</span>
                <span class="field-value">
                  ${complaint.transporteur || ""}
                </span>
              </div>

            </div>

            <div class="section">

              <div class="field">
                <span class="field-label">Problem:</span>
                <span class="field-value">
                  ${complaint.problem || ""}
                </span>
              </div>

            </div>

            <div class="section">

              <div class="field">
                <span class="field-label">Comment:</span>

                <p class="field-value">
                  ${safeComment}
                </p>
              </div>

              <div class="field">
                <span class="field-label">Quantity:</span>
                <span class="field-value">
                  ${formValues.quantity || ""}
                </span>
              </div>

              <div class="field">
                <span class="field-label">Unit:</span>
                <span class="field-value">
                  ${formValues.unit || ""}
                </span>
              </div>

              <div class="field">
                <span class="field-label">
                  Type de credit:
                </span>

                <span class="field-value">
                  ${
                    userState.lang === "EN"
                      ? formValues.typeCredit?.textEn || "N/A"
                      : formValues.typeCredit?.textFr || "N/A"
                  }
                </span>
              </div>

              <div class="field">
                <span class="field-label">
                  Credit:
                </span>

                <span class="field-value">
                  ${formValues.credit || 0}
                </span>
              </div>

            </div>

          </div>

        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      alert("Unable to open print window. Please allow popups.");

      return;
    }

    printWindow.document.write(printableContent);

    printWindow.document.close();

    printWindow.focus();

    printWindow.print();

    printWindow.close();
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (!open) {
    return null;
  }

  if (!complaint) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            ...style,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography>Loading complaint...</Typography>
        </Box>
      </Modal>
    );
  }

  // ============================================================
  // TOTAL DOCUMENTS
  // ============================================================

  const totalDocuments = images.length + files.length;

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={style} ref={modalContentRef}>
          {/* ======================================================
              HEADER
          ====================================================== */}

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Modify Complaint
              </Typography>

              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Complaint #{complaint.id}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handlePrint}
            >
              Print
            </Button>
          </Box>

          {/* ======================================================
              HIDDEN ADD FILE INPUT
          ====================================================== */}

          <input
            ref={addFilesInputRef}
            type="file"
            multiple
            accept="image/*,.pdf"
            onChange={handleFileChange}
            style={{
              display: "none",
            }}
          />

          {/* ======================================================
              HIDDEN REPLACE FILE INPUT
          ====================================================== */}

          <input
            ref={replaceFileInputRef}
            type="file"
            accept="image/*,.pdf"
            onChange={handleReplaceFile}
            style={{
              display: "none",
            }}
          />

          {/* ======================================================
              COMPLAINT INFORMATION
          ====================================================== */}

          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Complaint Information
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1.5,
                "@media (max-width: 600px)": {
                  gridTemplateColumns: "1fr",
                },
              }}
            >
              <Typography>
                <strong>ID:</strong> {complaint.id}
              </Typography>

              <Typography>
                <strong>Date:</strong> {complaint.date}
              </Typography>

              <Typography>
                <strong>Restaurant:</strong> {complaint.restaurant}
              </Typography>

              <Typography>
                <strong>Item:</strong> {complaint.product}
              </Typography>

              <Typography>
                <strong>Supplier:</strong> {complaint.supplier}
              </Typography>

              <Typography>
                <strong>Transporteur:</strong> {complaint.transporteur}
              </Typography>

              {complaint.noLot && (
                <Typography>
                  <strong>Numéro de lot:</strong> {complaint.noLot}
                </Typography>
              )}

              {complaint.productionDate && (
                <Typography>
                  <strong>Date de production:</strong>{" "}
                  {complaint.productionDate}
                </Typography>
              )}

              {complaint.expirationDate && (
                <Typography>
                  <strong>Date d'expiration:</strong> {complaint.expirationDate}
                </Typography>
              )}
            </Box>
          </Paper>

          {/* ======================================================
              PROBLEM
          ====================================================== */}

          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Problem
            </Typography>

            <Typography color="text.secondary">
              {complaint.problem || "No problem description."}
            </Typography>
          </Paper>

          {/* ======================================================
              EDITABLE FIELDS
          ====================================================== */}

          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Complaint Details
            </Typography>

            {/* STATUS */}

            <Autocomplete
              options={statusses}
              getOptionLabel={(option) =>
                userState.lang === "EN"
                  ? option?.textEn || ""
                  : option?.textFr || ""
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={formValues.status || null}
              onChange={(event, newValue) => {
                setFormValues((previous) => ({
                  ...previous,
                  status: newValue,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Status"
                  fullWidth
                  margin="normal"
                />
              )}
            />

            {/* COMMENT */}

            <TextField
              label="Comment"
              name="comment"
              value={formValues.comment || ""}
              onChange={handleInputChange}
              multiline
              rows={4}
              fullWidth
              margin="normal"
            />

            {/* QUANTITY / UNIT */}

            <Box
              display="flex"
              gap={2}
              mt={1}
              sx={{
                "@media (max-width: 600px)": {
                  flexDirection: "column",
                },
              }}
            >
              <TextField
                label="Quantity"
                name="quantity"
                type="number"
                value={formValues.quantity ?? ""}
                onChange={handleInputChange}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel id="unit-select-label">
                  {t("TypeQuantity")}
                </InputLabel>

                <Select
                  labelId="unit-select-label"
                  id="unit-select"
                  name="unit"
                  value={formValues.unit || ""}
                  label={t("TypeQuantity")}
                  onChange={(event) => {
                    setFormValues((previous) => ({
                      ...previous,
                      unit: event.target.value,
                    }));
                  }}
                >
                  <MenuItem value="unite">{t("Unite")}</MenuItem>

                  <MenuItem value="caisse">{t("Caisse")}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* CREDIT */}

            <Box
              display="flex"
              gap={2}
              mt={2}
              sx={{
                "@media (max-width: 600px)": {
                  flexDirection: "column",
                },
              }}
            >
              <Autocomplete
                options={creditTypes}
                getOptionLabel={(option) =>
                  userState.lang === "EN"
                    ? option?.textEn || ""
                    : option?.textFr || ""
                }
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                value={formValues.typeCredit || null}
                onChange={(event, newValue) => {
                  setFormValues((previous) => ({
                    ...previous,
                    typeCredit: newValue,
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Type de credit" fullWidth />
                )}
                sx={{
                  flex: 1,
                }}
              />

              <TextField
                label="Credit"
                name="credit"
                type="number"
                value={formValues.credit ?? ""}
                onChange={handleInputChange}
                fullWidth
                sx={{
                  flex: 1,
                }}
              />
            </Box>
          </Paper>

          {/* ======================================================
              DOCUMENTS SECTION
          ====================================================== */}

          <Paper
            variant="outlined"
            sx={{
              mt: 3,
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            {/* ====================================================
                DOCUMENT HEADER
            ==================================================== */}

            <Box
              sx={{
                p: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.default",
                "@media (max-width: 600px)": {
                  flexDirection: "column",
                  alignItems: "stretch",
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  }}
                >
                  <DescriptionOutlinedIcon />
                </Box>

                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" fontWeight={700}>
                      Documents
                    </Typography>

                    <Chip
                      label={totalDocuments}
                      size="small"
                      color="primary"
                      variant={totalDocuments > 0 ? "filled" : "outlined"}
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    Manage the documents attached to this complaint.
                  </Typography>
                </Box>
              </Box>

              {/* ==================================================
                  ALWAYS VISIBLE ADD BUTTON
              ================================================== */}

              <Button
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<UploadFileIcon />}
                onClick={handleOpenAddDocuments}
                sx={{
                  minWidth: 175,
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Add Documents
              </Button>
            </Box>

            {/* ====================================================
                ADD DOCUMENT AREA
            ==================================================== */}

            <Box sx={{ p: 2.5 }}>
              <Box
                onClick={handleOpenAddDocuments}
                sx={{
                  minHeight: 120,
                  border: "2px dashed",
                  borderColor: "primary.light",
                  borderRadius: 2.5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: "action.hover",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.selected",
                  },
                }}
              >
                <UploadFileIcon
                  sx={{
                    fontSize: 42,
                    color: "primary.main",
                    mb: 0.5,
                  }}
                />

                <Typography fontWeight={600}>Add documents</Typography>

                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  Click here or use the Add Documents button above
                </Typography>

                <Typography variant="caption" color="text.secondary" mt={0.5}>
                  Supported formats: images and PDF
                </Typography>
              </Box>

              {/* ==================================================
                  NEW DOCUMENTS
              ================================================== */}

              {files.length > 0 && (
                <Box mt={3}>
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <UploadFileIcon color="primary" />

                    <Typography fontWeight={700}>New Documents</Typography>

                    <Chip label={files.length} size="small" color="primary" />
                  </Box>

                  <Stack spacing={1.5}>
                    {files.map((file, index) => {
                      if (!file) {
                        return null;
                      }

                      const fileType = file.type || "";

                      const fileName = file.name || `File_${index + 1}`;

                      const isImage = fileType.startsWith("image/");

                      const isPDF = fileType === "application/pdf";

                      return (
                        <Box
                          key={`${fileName}-${index}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            p: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            backgroundColor: "background.paper",
                          }}
                        >
                          {isImage ? (
                            <LocalImagePreview
                              file={file}
                              fileName={fileName}
                            />
                          ) : isPDF ? (
                            <Box
                              sx={{
                                width: 90,
                                height: 90,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 2,
                                backgroundColor: "error.50",
                                border: "1px solid",
                                borderColor: "error.100",
                              }}
                            >
                              <PictureAsPdfIcon
                                sx={{
                                  fontSize: 48,
                                }}
                                color="error"
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                width: 90,
                                height: 90,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 2,
                                backgroundColor: "action.hover",
                              }}
                            >
                              <UploadFileIcon
                                sx={{
                                  fontSize: 45,
                                }}
                                color="action"
                              />
                            </Box>
                          )}

                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <Typography fontWeight={600} noWrap>
                              {fileName}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                              {isImage
                                ? "Image"
                                : isPDF
                                  ? "PDF document"
                                  : "Document"}
                            </Typography>

                            {file.size && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {(file.size / 1024).toFixed(1)} KB
                              </Typography>
                            )}
                          </Box>

                          <Tooltip title="Download">
                            <IconButton
                              color="primary"
                              onClick={() => handleDownloadLocalFile(file)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveFile(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* ==================================================
                  EXISTING DOCUMENTS
              ================================================== */}

              {images.length > 0 && (
                <Box mt={3}>
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <DescriptionOutlinedIcon color="action" />

                    <Typography fontWeight={700}>Existing Documents</Typography>

                    <Chip
                      label={images.length}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  <Stack spacing={1.5}>
                    {images.map((file, index) => {
                      if (!file || !file.url) {
                        return null;
                      }

                      const isReplacement = file.isReplacement === true;

                      const isPDF = file.type === "pdf";

                      return (
                        <Box
                          key={`${file.name || "file"}-${index}`}
                          sx={{
                            border: "1px solid",
                            borderColor: isReplacement
                              ? "warning.main"
                              : "divider",
                            borderRadius: 2,
                            p: 1.5,
                            backgroundColor: isReplacement
                              ? "warning.50"
                              : "background.paper",
                          }}
                        >
                          {/* REPLACEMENT */}

                          {isReplacement && (
                            <Box mb={1}>
                              <Chip
                                size="small"
                                color="warning"
                                label="Updated document"
                              />
                            </Box>
                          )}

                          <Box
                            display="flex"
                            alignItems="center"
                            gap={2}
                            sx={{
                              "@media (max-width: 600px)": {
                                flexDirection: "column",
                                alignItems: "stretch",
                              },
                            }}
                          >
                            {/* PREVIEW */}

                            {isPDF ? (
                              <Box
                                sx={{
                                  width: 90,
                                  height: 90,
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: 2,
                                  backgroundColor: "error.50",
                                  border: "1px solid",
                                  borderColor: "error.100",
                                }}
                              >
                                <PictureAsPdfIcon
                                  color="error"
                                  sx={{
                                    fontSize: 48,
                                  }}
                                />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: 90,
                                  height: 90,
                                  flexShrink: 0,
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  backgroundColor: "background.default",
                                }}
                              >
                                <img
                                  src={file.url}
                                  alt={file.name || `Image ${index + 1}`}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    handleDownloadExistingFile(file, index)
                                  }
                                />
                              </Box>
                            )}

                            {/* FILE INFORMATION */}

                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              <Typography fontWeight={600} noWrap>
                                {file.name ||
                                  (isPDF ? "PDF File" : `Image ${index + 1}`)}
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {isPDF ? "PDF document" : "Image"}
                              </Typography>

                              {isReplacement && file.replacedFileName && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Replacing: {file.replacedFileName}
                                </Typography>
                              )}
                            </Box>

                            {/* ACTIONS */}

                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              {isPDF && (
                                <Tooltip title="Open PDF">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleOpenPDF(file)}
                                  >
                                    Open
                                  </Button>
                                </Tooltip>
                              )}

                              <Tooltip title="Download">
                                <IconButton
                                  color="primary"
                                  onClick={() =>
                                    handleDownloadExistingFile(file, index)
                                  }
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Update document">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleOpenReplaceFile(index)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete document">
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    handleRemoveExistingFile(index)
                                  }
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* ==================================================
                  EMPTY STATE
              ================================================== */}

              {images.length === 0 && files.length === 0 && (
                <Box
                  mt={3}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "action.hover",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No documents are currently attached to this complaint.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* ======================================================
              ACTIONS
          ====================================================== */}

          <Box
            mt={4}
            pt={2}
            borderTop="1px solid"
            borderColor="divider"
            display="flex"
            justifyContent="space-between"
          >
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              Save Changes
            </Button>

            <Button variant="outlined" color="secondary" onClick={onClose}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ============================================================
          DELETE CONFIRMATION
      ============================================================ */}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "error.lighter",
              }}
            >
              <WarningAmberIcon color="error" />
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={700}>
                Delete document?
              </Typography>

              <Typography variant="body2" color="text.secondary">
                This action will remove the document from the complaint.
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          {fileToDelete && (
            <Box
              sx={{
                mt: 1,
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.default",
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                {fileToDelete.file?.type === "pdf" ? (
                  <PictureAsPdfIcon
                    color="error"
                    sx={{
                      fontSize: 45,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <img
                      src={fileToDelete.file?.url}
                      alt="File preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <Typography fontWeight={600} noWrap>
                    {fileToDelete.file?.name}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    This document will be removed from the current complaint.
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            gap: 1,
          }}
        >
          <Button variant="outlined" onClick={handleCancelDelete}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ModifyComplaint;
